import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Grid,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import DailyCheckIn from '../../models/dailyCheckIn';
import Account from '../../models/account';
import dayjs from 'dayjs';

const EditCheckIn = () => {
  const { checkInId } = useParams();
  const navigate = useNavigate();
  const [checkInData, setCheckInData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [checkInAt, setCheckInAt] = useState(null);
  const [checkOutAt, setCheckOutAt] = useState(null);

  const fetchCheckIn = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dailyCheckIn = new DailyCheckIn();
      const response = await dailyCheckIn.fetchById(checkInId);
      const record = response.data.data;
      if (record) {
        setCheckInData(record);
        setCheckInAt(record.check_in_at ? dayjs(record.check_in_at) : null);
        setCheckOutAt(record.check_out_at ? dayjs(record.check_out_at) : null);
      } else {
        setError('Không tìm thấy lịch sử điểm danh.');
      }
    } catch (err) {
      setError('Tải thông tin điểm danh thất bại.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [checkInId]);

  useEffect(() => {
    fetchCheckIn();
  }, [fetchCheckIn]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const account = new Account({ id: checkInData.account_id });

      const originalDate = dayjs(checkInData.date);

      const formatDateTime = (timeValue) => {
        if (!timeValue) return null;
        const time = dayjs(timeValue);
        return originalDate
          .hour(time.hour())
          .minute(time.minute())
          .format('YYYY-MM-DDTHH:mm');
      };

      await account.updateCheckIn(checkInId, {
        check_in_at: formatDateTime(checkInAt),
        check_out_at: formatDateTime(checkOutAt),
      });
      setSuccess('Cập nhật điểm danh thành công.');
      setTimeout(() => navigate('/check-in-history'), 2000);
    } catch (err) {
      setError('Cập nhật điểm danh thất bại.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom>
            Chỉnh sửa điểm danh
          </Typography>
          {checkInData && (
            <Typography variant="h6" gutterBottom>
              Nhân viên: {checkInData.account_name} - Ngày: {dayjs(checkInData.date).format('YYYY-MM-DD')}
            </Typography>
          )}
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TimePicker
                  label="Giờ vào"
                  value={checkInAt}
                  onChange={(newValue) => setCheckInAt(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  ampm={false}
                  timeSteps={{ minutes: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TimePicker
                  label="Giờ ra"
                  value={checkOutAt}
                  onChange={(newValue) => setCheckOutAt(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  ampm={false}
                  timeSteps={{ minutes: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Lưu'}
                </Button>
              </Grid>
            </Grid>
          </form>
          {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default EditCheckIn;
